import React, { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Loader2, Send } from 'lucide-react';
import MessageBubble from '../components/MessageBubble';
import FeedbackModifierCard from '../components/feedback/FeedbackModifierCard';
import { MicrophoneButton } from '../components/MicrophoneButton';
import { useVoiceInput } from '../hooks/useVoiceInput';
import { useAdminApi, type AdminOnboardingQuestionState, type FeedbackChatModifier } from '../hooks/useAdminApi';
import { startWordByWordReveal } from '../utils/startWordByWordReveal';

interface AdminOnboardingPageProps {
  isDarkMode: boolean;
}

interface OnboardingMessage {
  id: string;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
  memory?: FeedbackChatModifier;
  questionIndex?: number | null;
}

const AdminOnboardingPage: React.FC<AdminOnboardingPageProps> = ({ isDarkMode }) => {
  const {
    isAuthenticated,
    loading,
    error,
    login,
    startAdminOnboarding,
    sendAdminOnboardingMessage,
    applyAdminOnboardingMemory,
  } = useAdminApi();

  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [messages, setMessages] = useState<OnboardingMessage[]>([]);
  const [companyName, setCompanyName] = useState('Synaworks');
  const [agentName, setAgentName] = useState('Sally');
  const [adminName, setAdminName] = useState('Admin');
  const [preparedQuestions, setPreparedQuestions] = useState<string[]>([]);
  const [questionStates, setQuestionStates] = useState<AdminOnboardingQuestionState[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [sessionMode, setSessionMode] = useState<'questions' | 'free' | null>(null);
  const [inputText, setInputText] = useState('');
  const [bootstrapping, setBootstrapping] = useState(false);
  const [isWaitingForAssistant, setIsWaitingForAssistant] = useState(false);
  const [hasStartedStructuredFlow, setHasStartedStructuredFlow] = useState(false);
  const [isResettingConversation, setIsResettingConversation] = useState(false);
  const [pendingActionLogs, setPendingActionLogs] = useState<string[]>([]);
  const [memoryBusyMessageId, setMemoryBusyMessageId] = useState<string | null>(null);
  const hasStartedRef = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const initialTypingCancelRef = useRef<(() => void) | null>(null);
  const replyTypingCancelRef = useRef<(() => void) | null>(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const {
    isRecording,
    isTranscribing,
    waveformData,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useVoiceInput();

  const title = useMemo(() => `${companyName} - Onboarding Chat`, [companyName]);

  const isNearBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const { scrollTop, scrollHeight, clientHeight } = container;
    return scrollHeight - scrollTop - clientHeight < 20;
  };

  const scrollToBottom = (instant?: boolean) => {
    if (!shouldAutoScroll) return;
    const el = messagesEndRef.current;
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: instant ? 'auto' : 'smooth' });
    } catch (_error) {
      try {
        el.scrollIntoView();
      } catch (_ignored) {
        // Ignore unsupported browsers
      }
    }
  };

  const handleScroll = () => {
    const nearBottom = isNearBottom();
    if (!nearBottom) {
      setIsUserScrolling(true);
      setShouldAutoScroll(false);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        setIsUserScrolling(false);
        if (isNearBottom()) setShouldAutoScroll(true);
      }, 1000);
    } else {
      setIsUserScrolling(false);
      setShouldAutoScroll(true);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    }
  };

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      initialTypingCancelRef.current?.();
      replyTypingCancelRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (messages.length === 0) return;
    const lastMessage = messages[messages.length - 1];
    if (lastMessage.sender === 'user') {
      scrollToBottom();
      return;
    }
    if (shouldAutoScroll && !isUserScrolling) {
      scrollToBottom();
    }
  }, [messages, shouldAutoScroll, isUserScrolling]);

  useEffect(() => {
    if (!isAuthenticated || hasStartedRef.current) return;
    hasStartedRef.current = true;
    setMessages([
      {
        id: 'assistant-loading',
        sender: 'assistant',
        text: '',
        timestamp: new Date(),
      },
    ]);

    const bootstrap = async () => {
      setBootstrapping(true);
      const data = await startAdminOnboarding();
      setBootstrapping(false);
      if (!data) return;

      setCompanyName(data.company_name);
      setAgentName(data.agent_name);
      setAdminName(data.admin_name);
      setPreparedQuestions(data.questions);
      setQuestionStates(data.question_states || []);
      setCurrentQuestionIndex(typeof data.current_question_index === 'number' ? data.current_question_index : 0);
      setSessionMode(null);
      setMessages([
        {
          id: 'assistant-initial',
          sender: 'assistant',
          text: '',
          timestamp: new Date(),
        },
      ]);
      initialTypingCancelRef.current?.();
      initialTypingCancelRef.current = startWordByWordReveal(data.initial_message, {
        initialDelayMs: 0,
        wordDelayMs: 60,
        onUpdate: (nextText) => {
          setMessages((prev) => prev.map((message) => (
            message.id === 'assistant-initial'
              ? { ...message, text: nextText }
              : message
          )));
        },
        onComplete: () => {
          initialTypingCancelRef.current = null;
        },
      });

      console.group('[Admin Onboarding] Sally questions');
      data.questions.forEach((question, index) => {
        console.log(`${index + 1}. ${question}`);
      });
      console.groupEnd();
    };

    bootstrap();
  }, [isAuthenticated, startAdminOnboarding]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    await login(loginUser, loginPass);
  };

  const sendConversation = async (
    nextMessages: OnboardingMessage[],
    overrides?: {
      sessionMode?: 'questions' | 'free';
      currentQuestionIndex?: number;
      questionStates?: AdminOnboardingQuestionState[];
      allowMemoryGeneration?: boolean;
    },
  ) => {
    setIsWaitingForAssistant(true);
    const actionLogsToSend = [...pendingActionLogs];
    const requestSessionMode = overrides?.sessionMode || sessionMode || 'questions';
    const requestCurrentQuestionIndex = typeof overrides?.currentQuestionIndex === 'number'
      ? overrides.currentQuestionIndex
      : currentQuestionIndex;
    const requestQuestionStates = overrides?.questionStates || questionStates;
    const requestAllowMemoryGeneration = overrides?.allowMemoryGeneration ?? true;
    const response = await sendAdminOnboardingMessage(
      nextMessages.map((message) => ({
        id: message.id,
        role: message.sender,
        content: message.text,
        ...(message.memory ? { memory: message.memory } : {}),
      })),
      preparedQuestions,
      companyName,
      agentName,
      adminName,
      requestSessionMode,
      requestCurrentQuestionIndex,
      requestQuestionStates,
      requestAllowMemoryGeneration,
      actionLogsToSend,
    );
    setIsWaitingForAssistant(false);
    if (!response?.assistant_message) return;
    setPendingActionLogs([]);
    setSessionMode(response.session_mode);
    setQuestionStates(response.question_states || []);
    setCurrentQuestionIndex(typeof response.current_question_index === 'number' ? response.current_question_index : currentQuestionIndex);
    setShouldAutoScroll(true);
    setIsUserScrolling(false);
    const replyId = `assistant-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: replyId,
        sender: 'assistant',
        text: '',
        timestamp: new Date(),
        questionIndex: response.session_mode === 'questions' ? response.asked_question_index : null,
        ...(response.memory_draft?.text ? { memory: response.memory_draft } : {}),
      },
    ]);
    replyTypingCancelRef.current?.();
    replyTypingCancelRef.current = startWordByWordReveal(response.assistant_message, {
      initialDelayMs: 0,
      wordDelayMs: 60,
      onUpdate: (nextText) => {
        setMessages((prev) => prev.map((message) => (
          message.id === replyId ? { ...message, text: nextText } : message
        )));
      },
      onComplete: () => {
        replyTypingCancelRef.current = null;
      },
    });
  };

  const handleStartChoice = async (mode: 'questions' | 'free') => {
    if (hasStartedStructuredFlow || isWaitingForAssistant) return;
    const content = mode === 'questions'
      ? `Willkommen zum Onboarding, ${agentName}! Stell mir deine Fragen und ich werde sie so gut wie möglich beantworten.`
      : `Willkommen beim Onboarding, ${agentName}! Ich gebe dir direkt ein freies Onboarding.`;

    const userMessage: OnboardingMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: content,
      timestamp: new Date(),
    };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setSessionMode(mode);
    setHasStartedStructuredFlow(true);
    await sendConversation(nextMessages, {
      sessionMode: mode,
      currentQuestionIndex,
      questionStates,
      allowMemoryGeneration: false,
    });
  };

  const handleSkipQuestion = async () => {
    if (!hasStartedStructuredFlow || isWaitingForAssistant || sessionMode !== 'questions') return;

    // If the current question is still "pending", "Annehmen" already advanced the
    // index — don't advance again, just send so Sally asks this question.
    const currentStatus = questionStates[currentQuestionIndex]?.status;
    const alreadyAdvanced = currentStatus === 'pending' || currentStatus === undefined;

    const nextIndex = alreadyAdvanced ? currentQuestionIndex : currentQuestionIndex + 1;
    if (nextIndex >= preparedQuestions.length) return;

    const skipsIntoFreeMode = !alreadyAdvanced && nextIndex >= preparedQuestions.length;

    if (skipsIntoFreeMode) {
      setPendingActionLogs((prev) => [
        ...prev,
        `Der Admin hat Frage ${currentQuestionIndex + 1} übersprungen. Alle vorbereiteten Fragen sind damit abgeschlossen. Bitte wechsle in den freien Gesprächsmodus und schließe das Onboarding ab.`,
      ]);
      setSessionMode('free');
    } else if (!alreadyAdvanced) {
      setPendingActionLogs((prev) => [
        ...prev,
        `Der Admin hat Frage ${currentQuestionIndex + 1} übersprungen und möchte direkt zur nächsten Frage.`,
      ]);
      setCurrentQuestionIndex(nextIndex);
    }

    const userMessage: OnboardingMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: 'Weiter zur nächsten Frage bitte.',
      timestamp: new Date(),
    };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    await sendConversation(nextMessages, {
      sessionMode: skipsIntoFreeMode ? 'free' : (sessionMode || 'questions'),
      currentQuestionIndex: skipsIntoFreeMode ? currentQuestionIndex : nextIndex,
      allowMemoryGeneration: false,
    });
  };

  const handleSendMessage = async () => {
    if (!hasStartedStructuredFlow || isWaitingForAssistant || !inputText.trim()) return;
    const userMessage: OnboardingMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: inputText.trim(),
      timestamp: new Date(),
    };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInputText('');
    await sendConversation(nextMessages);
  };

  const updateMessageMemory = (messageId: string, updater: (current: FeedbackChatModifier) => FeedbackChatModifier) => {
    setMessages((prev) => prev.map((message) => {
      if (message.id !== messageId || !message.memory) return message;
      return {
        ...message,
        memory: updater(message.memory),
      };
    }));
  };

  const handleEditMemory = (messageId: string, nextText: string) => {
    const trimmed = nextText.trim();
    if (!trimmed) return;
    let previousText = '';
    updateMessageMemory(messageId, (current) => {
      previousText = current.text;
      return {
        ...current,
        text: trimmed,
        status: 'draft',
        edited_at: new Date().toISOString(),
      };
    });
    if (trimmed !== previousText) {
      setPendingActionLogs((prev) => [
        ...prev,
        `Der Admin hat den Onboarding-Gedächtnis-Eintrag "${previousText}" folgendermaßen editiert: "${trimmed}".`,
      ]);
    }
  };

  const handleAcceptMemory = async (messageId: string) => {
    const targetMessage = messages.find((message) => message.id === messageId);
    const memory = targetMessage?.memory;
    if (!memory?.text || memory.status === 'accepted') return;

    setMemoryBusyMessageId(messageId);
    const result = await applyAdminOnboardingMemory(memory.text, messageId);
    setMemoryBusyMessageId(null);
    if (!result) return;

    updateMessageMemory(messageId, (current) => ({
      ...current,
      status: 'accepted',
      accepted_at: new Date().toISOString(),
      applied_scope: result.scope || null,
      applied_workspace_slug: result.workspace_slug || null,
    }));
    const isLastQuestion = targetMessage && typeof targetMessage.questionIndex === 'number'
      && targetMessage.questionIndex === preparedQuestions.length - 1;
    setPendingActionLogs((prev) => [
      ...prev,
      `Der Admin hat den Onboarding-Gedächtnis-Eintrag "${memory.text}" angenommen.`,
      ...(isLastQuestion ? ['Alle vorbereiteten Fragen wurden gestellt. Das strukturierte Q&A ist abgeschlossen. Bitte wechsle in den freien Gesprächsmodus und schließe das Onboarding ab.'] : []),
    ]);
    if (targetMessage && typeof targetMessage.questionIndex === 'number' && targetMessage.questionIndex === currentQuestionIndex) {
      if (isLastQuestion) {
        setSessionMode('free');
      } else {
        setCurrentQuestionIndex((prev) => Math.min(prev + 1, preparedQuestions.length - 1));
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSendMessage();
    }
  };

  const handleMicrophoneClick = async () => {
    if (isRecording) {
      const transcript = await stopRecording();
      if (transcript) {
        setInputText((prev) => {
          const existing = prev || '';
          const nextPart = transcript.trim();
          if (!nextPart) return existing;
          if (!existing.trim()) return nextPart;
          const separator = /[\s\n]$/.test(existing) ? '' : ' ';
          return `${existing}${separator}${nextPart}`;
        });
      }
    } else {
      await startRecording();
    }
  };

  if (!isAuthenticated) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-950' : 'bg-gray-100'}`}>
        <div className={`w-full max-w-sm ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'} border rounded-lg shadow-lg`}>
          <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-blue-400' : 'bg-blue-500'}`} />
              <h1 className={`text-sm font-semibold tracking-wide uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Admin Onboarding
              </h1>
            </div>
            <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Melde dich an, um das Onboarding mit Sally zu starten.
            </p>
          </div>
          <form onSubmit={handleLogin} className="p-6 space-y-4">
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Username</label>
              <input
                type="text"
                value={loginUser}
                onChange={(e) => setLoginUser(e.target.value)}
                className={`w-full px-3 py-2 text-sm rounded border outline-none transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500'}`}
                autoFocus
              />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Password</label>
              <input
                type="password"
                value={loginPass}
                onChange={(e) => setLoginPass(e.target.value)}
                className={`w-full px-3 py-2 text-sm rounded border outline-none transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500'}`}
              />
            </div>
            {error && (
              <p className="text-xs text-red-400 bg-red-900/20 border border-red-800/30 rounded px-3 py-2">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-2 text-sm font-medium rounded transition-colors ${loading ? 'opacity-50 cursor-wait' : ''} ${isDarkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex min-w-0 items-center gap-3">
          <a
            href="/admin?tab=behavior"
            className={`shrink-0 px-3 py-2 rounded-lg text-sm border transition-colors ${isDarkMode ? 'border-gray-700 text-gray-300 hover:text-white hover:border-gray-600 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:text-gray-900 hover:border-gray-400 hover:bg-gray-100'}`}
          >
            Zurück zu Admin Panel
          </a>
          <div className="min-w-0">
            <h1 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <span className="block truncate">{title}</span>
            </h1>
            <p className={`hidden text-sm sm:flex items-center gap-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Erstes Mitarbeitergespräch mit {agentName}
            </p>
          </div>
        </div>

        <button
          onClick={() => {
            setIsResettingConversation(true);
            flushSync(() => {
              setMessages([
                {
                  id: 'assistant-loading',
                  sender: 'assistant',
                  text: '',
                  timestamp: new Date(),
                },
              ]);
              setPreparedQuestions([]);
              setQuestionStates([]);
              setCurrentQuestionIndex(0);
              setSessionMode(null);
              setInputText('');
              setHasStartedStructuredFlow(false);
              setIsWaitingForAssistant(false);
              setPendingActionLogs([]);
              setMemoryBusyMessageId(null);
            });
            initialTypingCancelRef.current?.();
            initialTypingCancelRef.current = null;
            replyTypingCancelRef.current?.();
            replyTypingCancelRef.current = null;
            hasStartedRef.current = false;
            setTimeout(() => setIsResettingConversation(false), 100);
          }}
          className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${isDarkMode ? 'border-gray-700 text-gray-400 hover:text-white hover:border-gray-600' : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'}`}
        >
          Reset
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 flex flex-col min-w-0 relative">
          <div className="flex-1 overflow-y-auto" ref={messagesContainerRef} onScroll={handleScroll}>
            <div className="px-2 pb-2 pt-4 sm:px-4 sm:pb-4 sm:pt-6">
              <div className="max-w-4xl mx-auto space-y-3 sm:space-y-4">
                {messages.map((message) => (
                  message.text ? (
                    <div key={message.id}>
                      <MessageBubble
                        message={message}
                        isDarkMode={isDarkMode}
                      />
                      {message.sender === 'assistant' && sessionMode === 'questions' && typeof message.questionIndex === 'number' ? (
                        <div className="px-2 sm:px-0">
                          <div className={`ml-9 sm:ml-11 mt-1 text-[11px] uppercase tracking-[0.12em] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            Frage {message.questionIndex + 1}/{preparedQuestions.length || 12}
                          </div>
                        </div>
                      ) : null}
                      {message.sender === 'assistant' && message.memory?.text ? (
                        <div className="px-2 sm:px-0">
                          <div className="ml-9 sm:ml-11 max-w-xs sm:max-w-2xl lg:max-w-3xl">
                            <FeedbackModifierCard
                              modifier={message.memory}
                              isDarkMode={isDarkMode}
                              isBusy={memoryBusyMessageId === message.id}
                              onAccept={() => void handleAcceptMemory(message.id)}
                              onEdit={(nextText) => handleEditMemory(message.id, nextText)}
                              title="Onboarding-Gedächtnis"
                              acceptedLinkHref="/admin?tab=behavior"
                              acceptedLinkLabel="In Behavior öffnen"
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null
                ))}

                {bootstrapping && (
                  <div className="px-2 sm:px-0">
                    <div className={`max-w-xs sm:max-w-2xl lg:max-w-3xl ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <div className="flex gap-2 sm:gap-3">
                        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          <Loader2 size={16} className="animate-spin" />
                        </div>
                        <div className={`rounded-xl sm:rounded-2xl rounded-bl-md p-3 sm:p-4 text-sm sm:text-base ${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-gray-100 text-gray-900'}`}>
                          Einen Moment, ich bereite mich auf das Gespräch vor...
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isWaitingForAssistant && !bootstrapping && (
                  <div className="px-2 sm:px-0">
                    <div className={`max-w-xs sm:max-w-2xl lg:max-w-3xl ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <div className="flex gap-2 sm:gap-3">
                        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                        <div className={`rounded-xl sm:rounded-2xl rounded-bl-md p-3 sm:p-4 text-sm sm:text-base ${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-gray-100 text-gray-900'}`}>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-current rounded-full animate-bounce" />
                            <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                            <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>
          </div>

          <div className={`border-t ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="max-w-4xl mx-auto p-3 sm:p-4">
              {!bootstrapping && hasStartedStructuredFlow && sessionMode === 'questions' && currentQuestionIndex < preparedQuestions.length - 1 && (
                <div className="mb-3 flex">
                  <button
                    type="button"
                    onClick={() => void handleSkipQuestion()}
                    disabled={isWaitingForAssistant}
                    className={`rounded-full px-4 py-1.5 text-xs font-medium transition-all border disabled:opacity-50 ${
                      isDarkMode
                        ? 'border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-500'
                        : 'border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    Nächste Frage
                  </button>
                </div>
              )}

              {!bootstrapping && !hasStartedStructuredFlow && messages.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => void handleStartChoice('questions')}
                    disabled={loading || isWaitingForAssistant}
                    className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${
                      isDarkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-100 border border-gray-600'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300'
                    } disabled:opacity-50`}
                  >
                    Stell mir deine Fragen (strukturiert).
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleStartChoice('free')}
                    disabled={loading || isWaitingForAssistant}
                    className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${
                      isDarkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-100 border border-gray-600'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300'
                    } disabled:opacity-50`}
                  >
                    Ich teile dir mit, was mir wichtig ist (unstrukturiert).
                  </button>
                </div>
              )}

              <div className={`flex-1 min-w-0 relative rounded-xl sm:rounded-2xl border-2 transition-all ${isDarkMode ? 'bg-gray-700 border-gray-600 focus-within:border-blue-500' : 'bg-gray-50 border-gray-200 focus-within:border-blue-500'}`}>
                <div className="flex items-center">
                  {!isRecording && !isTranscribing && (
                    <textarea
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={
                        hasStartedStructuredFlow
                          ? `Antworte ${agentName} oder ergänze weiteres Wissen über ${companyName}...`
                          : 'Wähle zuerst eine der beiden Onboarding-Optionen oben aus.'
                      }
                      rows={1}
                      spellCheck={false}
                      disabled={!hasStartedStructuredFlow || bootstrapping || isWaitingForAssistant}
                      className={`flex-1 min-w-0 resize-none rounded-l-xl sm:rounded-l-2xl px-3 sm:px-6 py-3 sm:py-4 text-base sm:text-lg focus:outline-none ${isDarkMode ? 'bg-transparent text-white placeholder-gray-400 disabled:text-gray-500' : 'bg-transparent text-gray-900 placeholder-gray-500 disabled:text-gray-400'}`}
                      style={{ minHeight: '56px' }}
                    />
                  )}
                  <div className={`flex items-center gap-1 m-1 sm:m-2 ${isRecording || isTranscribing ? 'flex-1' : 'flex-shrink-0'}`}>
                    <MicrophoneButton
                      isRecording={isRecording}
                      isTranscribing={isTranscribing}
                      waveformData={waveformData}
                      onClick={handleMicrophoneClick}
                      onCancel={cancelRecording}
                      disabled={!hasStartedStructuredFlow || bootstrapping || isWaitingForAssistant}
                      wide={isRecording || isTranscribing}
                    />
                  </div>
                  <button
                    onClick={() => void handleSendMessage()}
                    disabled={!hasStartedStructuredFlow || !inputText.trim() || bootstrapping || isWaitingForAssistant}
                    className={`p-2.5 sm:p-4 m-1 sm:m-2 rounded-lg sm:rounded-xl transition-all flex-shrink-0 ${!hasStartedStructuredFlow || !inputText.trim() || bootstrapping || isWaitingForAssistant ? 'opacity-50 cursor-not-allowed' : isDarkMode ? 'bg-blue-600 hover:bg-blue-700 text-white hover:scale-105' : 'bg-blue-500 hover:bg-blue-600 text-white hover:scale-105'}`}
                  >
                    <Send size={20} className="sm:hidden" />
                    <Send size={26} className="hidden sm:block" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOnboardingPage;
